import { useMutation, useQuery } from '@apollo/client';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate, useParams } from 'react-router-dom';

import { ProposalsIcon, TrashIcon } from '../assets';
import { Breadcrumbs } from '../components/common/Breadcrumbs';
import { Button } from '../components/common/Button';
import { ProposalSidebarCard } from '../components/proposals/ProposalSidebarCard';
import { SingleProposalTabs } from '../components/proposals/SingleProposalTabs';
import { ROUTES } from '../config/routes/config';
import { Confirm } from '../context/confirm/confirm';
import {
  APPROVE_PROPOSAL,
  DELETE_PROPOSAL,
  GENERATE_PROPOSAL_DOC,
  GENERATE_PROPOSAL_PDF,
  UPDATE_PROPOSAL,
} from '../graphql/proposal/mutation';
import { GET_PROPOSAL } from '../graphql/proposal/query';
import { useNotification } from '../hooks/useNotification';
import { usePermissions } from '../hooks/usePermissions';
import type {
  ProposalData,
  ResApproveProposal,
  ResDeleteProposal,
  ResGenerateProposalDoc,
  ResGenerateProposalPdf,
  ResProposal,
  ResUpdateProposal,
} from '../types/proposal';

export type SingleProposalOutletContext = {
  proposal: ProposalData;
  refetch: () => void;
  updateProposal: ReturnType<typeof useMutation<ResUpdateProposal>>[0];
  approveProposal: ReturnType<typeof useMutation<ResApproveProposal>>[0];
  deleteProposal: ReturnType<typeof useMutation<ResDeleteProposal>>[0];
  generateProposalPdf: ReturnType<
    typeof useMutation<ResGenerateProposalPdf>
  >[0];
  generateProposalDoc: ReturnType<
    typeof useMutation<ResGenerateProposalDoc>
  >[0];
  updating: boolean;
  approving: boolean;
  deleting: boolean;
  generating: boolean;
  generatingDoc: boolean;
};

function SingleProposal() {
  const { proposalId } = useParams<{ proposalId: string }>();
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const {
    notifyWarning,
    notifySaveSuccess,
    notifySaveError,
    notifySuccess,
    notifyError,
  } = useNotification();
  const { isInfluentia } = usePermissions();
  const location = window.location.pathname.split('/').pop() || '';
  const [open, setOpen] = useState(false);

  const { data, refetch } = useQuery<ResProposal>(GET_PROPOSAL, {
    variables: { proposalId },
    skip: !proposalId,
    onError: () => {
      notifyWarning(t('proposals.errorNotFound'));
      navigate(ROUTES.PROPOSALS);
    },
  });

  const proposal: ProposalData | undefined = data?.proposal?.data;

  const [approveProposal, { loading: approving }] =
    useMutation<ResApproveProposal>(APPROVE_PROPOSAL);
  const [deleteProposal, { loading: deleting }] =
    useMutation<ResDeleteProposal>(DELETE_PROPOSAL);
  const [updateProposal, { loading: updating }] =
    useMutation<ResUpdateProposal>(UPDATE_PROPOSAL);
  const [generateProposalPdf, { loading: generating }] =
    useMutation<ResGenerateProposalPdf>(GENERATE_PROPOSAL_PDF, {
      refetchQueries: [{ query: GET_PROPOSAL, variables: { proposalId } }],
      onCompleted: (response) =>
        response.generateProposalPdf.code === 200
          ? notifySuccess(t('proposals.generateProposalSuccess'))
          : notifyError(t('proposals.generateProposalError')),
      onError: () => notifyError(t('proposals.generateProposalError')),
    });
  const [generateProposalDoc, { loading: generatingDoc }] =
    useMutation<ResGenerateProposalDoc>(GENERATE_PROPOSAL_DOC, {
      refetchQueries: [{ query: GET_PROPOSAL, variables: { proposalId } }],
      onCompleted: (response) =>
        response.generateProposalDoc.code === 200
          ? notifySuccess(t('proposals.generateVerticalDocSuccess'))
          : notifyError(t('proposals.generateVerticalDocError')),
      onError: () => notifyError(t('proposals.generateVerticalDocError')),
    });

  const deleteProposalDB = () => {
    deleteProposal({
      variables: { id: proposalId },
      onCompleted: () => {
        notifySaveSuccess();
        navigate(ROUTES.PROPOSALS);
      },
      onError: () => {
        notifySaveError();
      },
    });
  };

  if (!proposal) return null;

  const context: SingleProposalOutletContext = {
    proposal,
    refetch,
    updateProposal,
    approveProposal,
    deleteProposal,
    generateProposalPdf,
    generateProposalDoc,
    updating,
    approving,
    deleting,
    generating,
    generatingDoc,
  };

  return (
    <div className="flex flex-row gap-x-5">
      <Confirm
        open={open}
        onConfirm={() => deleteProposalDB()}
        setOpen={setOpen}
        type="delete"
        title={t('global.deleteTitle')}
        subtitle={t('global.deleteSubtitle')}
        confirmText={t('global.delete')}
        cancelText={t('global.cancel')}
      />
      <div className="w-full flex flex-col gap-y-5">
        <Breadcrumbs
          icon={<ProposalsIcon />}
          breadcrumbs={[
            { text: t('proposals.title'), link: ROUTES.PROPOSALS },
            { text: proposal.name },
          ]}
        />

        <SingleProposalTabs
          proposalId={proposalId!}
          influencers={proposal.ProposalUsers}
        />

        <Outlet context={context} />
      </div>
      <div className="">
        <div
          className={`flex items-end justify-end ${location === 'campaign' && isInfluentia() ? '' : 'hidden'}`}
        >
          <Button
            text={t('proposals.delete')}
            id="deleteProposal"
            style="dark-blue"
            customClassnames="!px-2 !text-light-blue hover:bg-red-status hover:!text-white group"
            iconLeft={
              <TrashIcon className="text-red-status group-hover:text-white" />
            }
            loading={deleting}
            onClick={() => setOpen(true)}
          />
        </div>
        <div
          className={`mt-12 lg:block hidden ${location !== 'general' && 'lg:w-[420px]'}`}
        >
          {location !== 'general' && (
            <ProposalSidebarCard proposal={proposal} refetch={refetch} />
          )}
        </div>
      </div>
    </div>
  );
}

export default SingleProposal;
